import { Injectable } from '@angular/core';
import { openDB, type DBSchema } from 'idb';

import { UploadMediaItem, UploadStatus } from '#models/db/asset/upload.model';

export type { UploadMediaItem } from '#models/db/asset/upload.model';

const STORE_NAME = 'uploads';

interface UploadDB extends DBSchema {
  uploads: {
    key: number;
    value: UploadMediaItem;
    indexes: {
      'by-status': string;
      'by-batchId': string;
    };
  };
}

/**
 * Normalizes legacy records that lack the new upload-manager fields.
 * This ensures backward compatibility after the DB schema migration.
 */
function normalizeItem(item: UploadMediaItem): UploadMediaItem {
  return {
    ...item,
    status: item.status ?? (item.uploaded ? UploadStatus.Completed : UploadStatus.Pending),
    progress: item.progress ?? (item.uploaded ? 1 : 0),
    retryCount: item.retryCount ?? 0,
    maxRetries: item.maxRetries ?? 3,
    addedAt: item.addedAt ?? 0,
    fileSize: item.fileSize ?? item.file?.size ?? 0,
    batchId: item.batchId ?? 'legacy',
  };
}

@Injectable({
  providedIn: 'root',
})
export class UploadQueueService {
  private db = openDB<UploadDB>('upload-db', 2, {
    upgrade(db, oldVersion, _newVersion, transaction) {
      if (oldVersion < 1) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex('by-status', 'status');
        store.createIndex('by-batchId', 'batchId');
      } else if (oldVersion < 2) {
        const store = transaction.objectStore(STORE_NAME);

        if (!store.indexNames.contains('by-status')) {
          store.createIndex('by-status', 'status');
        }
        if (!store.indexNames.contains('by-batchId')) {
          store.createIndex('by-batchId', 'batchId');
        }
      }
    },
  });

  /**
   * Adds a file to the upload queue and stores its metadata in the database.
   *
   * @param item - The file to be uploaded.
   * @param objectName - The metadata objectName, `{user_id}/{id?}/file_name.{ext}`.
   * @param bucketName - The name of the bucket where the file will be uploaded.
   * @returns A promise that resolves to the unique ID of the newly added upload item.
   *
   * @remarks
   * - memory-asset-uploads: `{user_id}/{memory_id}/file_name.{ext}`
   * - memory-thumbnail-uploads: `{user_id}/{memory_id}/file_name.{ext}`
   * - vault-asset-uploads: `{user_id}/{document_id}/file_name.{ext}`
   * - profile-picture-uploads: `{user_id}/file_name.{ext}`
   */
  async add(item: UploadMediaItem): Promise<number> {
    const db = await this.db;

    const uploadItem: UploadMediaItem = {
      ...item,
      uploaded: false,
      status: item.status ?? UploadStatus.Pending,
      progress: item.progress ?? 0,
      retryCount: item.retryCount ?? 0,
      maxRetries: item.maxRetries ?? 3,
      addedAt: item.addedAt ?? Date.now(),
      fileSize: item.fileSize ?? item.file?.size ?? 0,
      batchId: item.batchId ?? 'legacy',
    };

    const id = await db.add(STORE_NAME, uploadItem);

    return id;
  }

  /**
   * Retrieves all upload media items from the 'uploads' object store in the database.
   *
   * @returns {Promise<UploadMediaItem[]>} A promise that resolves to an array of all `UploadMediaItem` objects.
   */
  async getAll(): Promise<UploadMediaItem[]> {
    const db = await this.db;
    const items = await db.getAll(STORE_NAME);

    return items.map(normalizeItem);
  }

  /**
   * Retrieves upload items filtered by status.
   */
  async getByStatus(status: UploadStatus): Promise<UploadMediaItem[]> {
    const db = await this.db;

    try {
      const items = await db.getAllFromIndex(STORE_NAME, 'by-status', status);
      return items.map(normalizeItem);
    } catch {
      // Fallback for legacy DB without indexes
      const all = await this.getAll();
      return all.filter((item) => item.status === status);
    }
  }

  /**
   * Retrieves upload items filtered by batch ID.
   */
  async getByBatchId(batchId: string): Promise<UploadMediaItem[]> {
    const db = await this.db;

    try {
      const items = await db.getAllFromIndex(STORE_NAME, 'by-batchId', batchId);
      return items.map(normalizeItem);
    } catch {
      // Fallback for legacy DB without indexes
      const all = await this.getAll();
      return all.filter((item) => item.batchId === batchId);
    }
  }

  /**
   * Updates an existing `UploadMediaItem` in the 'uploads' object store of the database.
   * If the item does not exist, it will be added.
   *
   * @param item - The `UploadMediaItem` to update or insert into the database.
   * @returns A promise that resolves when the operation is complete.
   */
  async update(item: UploadMediaItem): Promise<void> {
    const db = await this.db;

    await db.put(STORE_NAME, item);
  }

  /**
   * Updates only the status of a queue item.
   */
  async updateStatus(id: number, status: UploadStatus, errorMessage?: string): Promise<void> {
    const db = await this.db;
    const item = await db.get(STORE_NAME, id);

    if (item) {
      item.status = status;
      if (errorMessage !== undefined) {
        item.errorMessage = errorMessage;
      }
      await db.put(STORE_NAME, item);
    }
  }

  /**
   * Increments the retry count for an item and resets status to Pending.
   */
  async incrementRetry(id: number): Promise<boolean> {
    const db = await this.db;
    const item = await db.get(STORE_NAME, id);

    if (!item) return false;

    const normalized = normalizeItem(item);
    const retryCount = (normalized.retryCount ?? 0) + 1;
    const maxRetries = normalized.maxRetries ?? 3;

    if (retryCount > maxRetries) {
      return false;
    }

    normalized.retryCount = retryCount;
    normalized.status = UploadStatus.Pending;
    normalized.errorMessage = undefined;
    await db.put(STORE_NAME, normalized);

    return true;
  }

  /**
   * Updates the progress of an upload item.
   */
  async updateProgress(id: number, progress: number): Promise<void> {
    const db = await this.db;
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const item = await store.get(id);

    if (item && item.status === UploadStatus.Uploading) {
      item.progress = progress;
      store.put(item);
    }

    await tx.done;
  }

  /**
   * Removes all items from the queue.
   */
  async clearAll(): Promise<void> {
    const db = await this.db;
    await db.clear(STORE_NAME);
  }

  /**
   * Removes all completed items from the queue.
   */
  async clearCompleted(): Promise<void> {
    const completed = await this.getByStatus(UploadStatus.Completed);

    const db = await this.db;
    for (const item of completed) {
      if (item.id) {
        await db.delete(STORE_NAME, item.id);
      }
    }
  }

  /**
   * Retrieves all failed items from the queue.
   */
  async getFailedItems(): Promise<UploadMediaItem[]> {
    return this.getByStatus(UploadStatus.Failed);
  }

  /**
   * Removes an upload entry from the database by its unique identifier.
   *
   * @param id - The unique identifier of the upload entry to remove.
   * @returns A promise that resolves when the entry has been deleted.
   */
  async remove(id: number): Promise<void> {
    const db = await this.db;

    await db.delete(STORE_NAME, id);
  }
}
